import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  useStartOtpCycleMutation,
  useResendOtpCycleMutation,
  useVerifyOtpCycleMutation,
} from '../services/discountApi';
import { setOtpCycle, clearOtpCycle } from '../store/discountSlice';
import { useToast } from '../components/common/ToastProvider';

const CYCLE_MS = 300 * 1000;
const RESEND_COOLDOWN_SECONDS = 30;
const MAX_CYCLES = 3;

// Drives the discount-approval OTP cycle (start/resend/verify, countdown,
// resend cooldown). The in-flight cycle itself (id/number/recipients/expiry)
// lives in discountSlice, not component state — DiscountDrawer.js (where a
// cycle starts) and ApprovalBanner.js (which may pick the same cycle back up
// after the cashier hands off to "keep browsing") are siblings, not
// parent/child, so this is the one piece of state that has to survive that
// hand-off. Everything else here (countdown ticking, cooldown, terminal
// messages) is safe to re-derive fresh in whichever component mounts.
export function useDiscountOtpCycle(requestId) {
  const dispatch = useDispatch();
  const showToast = useToast();
  const cycle = useSelector((s) => s.discount.otpCycle);

  const [view, setView] = useState(cycle ? 'active' : 'idle'); // idle | active | expired | no_approver | exhausted | resolved
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resolvedByName, setResolvedByName] = useState('');

  const countdownRef = useRef(null);
  const cooldownRef = useRef(null);

  const [startOtpCycle, { isLoading: isStarting }] = useStartOtpCycleMutation();
  const [resendOtpCycle, { isLoading: isResending }] = useResendOtpCycleMutation();
  const [verifyOtpCycle, { isLoading: isVerifying }] = useVerifyOtpCycleMutation();

  useEffect(() => () => {
    clearInterval(countdownRef.current);
    clearInterval(cooldownRef.current);
  }, []);

  // BUG FIX: `view` only ever moved idle -> active/resolved/etc, never back
  // down — so a second, unrelated approval flow in the same session (this
  // hook's component, e.g. DiscountApprovalModal, stays mounted the whole
  // time) inherited whatever view a PRIOR requestId left behind (e.g. stuck
  // showing code entry or "Approved" for a brand-new request that hasn't
  // even started a cycle yet). A new requestId unambiguously means "fresh
  // flow" — reset local state to match. `cycle` itself is reset to null by
  // discountSlice's setPendingApproval in the same dispatch that changes
  // requestId, so `cycle` here reflects that by the time this runs.
  useEffect(() => {
    clearInterval(countdownRef.current);
    clearInterval(cooldownRef.current);
    // eslint-disable-next-line
    setView(cycle ? 'active' : 'idle');
    setSecondsLeft(0);
    setResendCooldown(0);
    setResolvedByName('');
    // eslint-disable-next-line
  }, [requestId]);

  // Recomputes the countdown from the stored (absolute) expiry — covers both
  // a fresh start in this component and a hand-off where a different
  // component's instance of this hook picks the same cycle back up.
  useEffect(() => {
    clearInterval(countdownRef.current);
    if (!cycle?.expiresAt) return undefined;
    setView((v) => (v === 'idle' ? 'active' : v));
    const compute = () => Math.max(0, Math.round((cycle.expiresAt - Date.now()) / 1000));
    setSecondsLeft(compute());
    countdownRef.current = setInterval(() => {
      const left = compute();
      setSecondsLeft(left);
      if (left <= 0) {
        clearInterval(countdownRef.current);
        setView('expired');
      }
    }, 1000);
    return () => clearInterval(countdownRef.current);
  }, [cycle?.id, cycle?.expiresAt]);

  const startCooldown = () => {
    clearInterval(cooldownRef.current);
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((s) => {
        if (s <= 1) { clearInterval(cooldownRef.current); return 0; }
        return s - 1;
      });
    }, 1000);
  };

  const start = useCallback(async () => {
    try {
      const res = await startOtpCycle(requestId).unwrap();
      const otpCycle = res?.data?.otp_cycle;
      const nextCycle = {
        id: otpCycle.id,
        cycleNumber: otpCycle.cycle_number,
        recipients: otpCycle.recipients || [],
        expiresAt: Date.now() + CYCLE_MS,
      };
      dispatch(setOtpCycle(nextCycle));
      setView('active');
      startCooldown();
      showToast(`6-digit codes texted to ${nextCycle.recipients.length} verified approvers`);
    } catch (err) {
      const message = err?.data?.error || '';
      if (message.includes('No verified approver') || message.includes('not available')) {
        setView('no_approver');
      } else if (message.includes('Maximum OTP cycles')) {
        setView('exhausted');
      } else {
        showToast(message || 'Could not start OTP approval', 'error');
      }
    }
  }, [requestId, startOtpCycle, dispatch, showToast]);

  const resend = useCallback(async () => {
    if (!cycle) return;
    try {
      await resendOtpCycle({ requestId, cycleId: cycle.id }).unwrap();
      dispatch(setOtpCycle({ ...cycle, expiresAt: Date.now() + CYCLE_MS }));
      startCooldown();
      showToast('New code sent to the same approvers');
    } catch (err) {
      showToast(err?.data?.error || 'Could not resend the code', 'error');
    }
  }, [requestId, cycle, resendOtpCycle, dispatch, showToast]);

  const verify = useCallback(async (code) => {
    if (!cycle) return { ok: false, error: 'No active cycle' };
    try {
      const res = await verifyOtpCycle({ requestId, cycleId: cycle.id, code }).unwrap();
      const req = res?.data?.discount_approval_request;
      clearInterval(countdownRef.current);
      dispatch(clearOtpCycle());
      setResolvedByName(req?.approved_by_name || '');
      setView('resolved');
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err?.data?.error || 'Incorrect verification code' };
    }
  }, [requestId, cycle, verifyOtpCycle, dispatch]);

  const reset = useCallback(() => {
    clearInterval(countdownRef.current);
    clearInterval(cooldownRef.current);
    dispatch(clearOtpCycle());
    setView('idle');
    setResolvedByName('');
  }, [dispatch]);

  return {
    view, cycle, secondsLeft, resendCooldown, resolvedByName,
    isStarting, isResending, isVerifying,
    canStartNewCycle: !cycle || cycle.cycleNumber < MAX_CYCLES,
    start, resend, verify, reset,
  };
}
